import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon, Calendar, Sparkles, MessageSquare, User, MessageCircle, Orbit, Heart } from 'lucide-react';

interface NavItem {
    label: string;
    href?: string;
    id?: string;
    icon: any;
}

interface MobileBottomNavProps {
    activeTab?: string;
    onTabChange?: (id: string) => void;
    tabs?: NavItem[];
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ activeTab, onTabChange, tabs }) => {
    const location = useLocation();
    const [mounted, setMounted] = useState(false);

    // Ensure we only create portal after component mounts (for SSR compatibility)
    useEffect(() => {
        setMounted(true);
    }, []);

    // Hide bottom nav on chat page
    if (location.pathname.startsWith('/chat')) {
        return null;
    }

    const isDashboard = location.pathname.startsWith('/dashboard');
    const isProfile = location.pathname.startsWith('/profile');
    const isAdmin = location.pathname.startsWith('/admin');

    const publicNavItems: NavItem[] = [
        { label: 'Kundli', href: '/free-kundli', icon: Sun },
        { label: 'Matching', href: '/kundli-matching', icon: Heart },
        { label: 'Horoscope', href: '/horoscope', icon: Moon },
        { label: 'Panchang', href: '/panchang', icon: Calendar },
        { label: 'Discussion', href: '/discussion', icon: MessageCircle },
    ];

    // Use provided tabs or default dashboard tabs.
    // Fall back to links when no tab-change handler is available.
    const dashboardNavItems: NavItem[] = tabs || (onTabChange ? [
        { label: 'Services', id: 'services', icon: Sparkles },
        { label: 'Chats', id: 'chats', icon: MessageSquare },
        { label: 'Insights', id: 'insights', icon: Orbit },
        { label: 'Kundlis', id: 'kundlis', icon: User },
    ] : [
        { label: 'Services', href: '/dashboard/services', icon: Sparkles },
        { label: 'Chats', href: '/dashboard/chats', icon: MessageSquare },
        { label: 'Insights', href: '/dashboard/insights', icon: Orbit },
        { label: 'Kundlis', href: '/dashboard/kundlis', icon: User },
    ]);

    const navItems = (isDashboard || isProfile || isAdmin) ? dashboardNavItems : publicNavItems;

    const navContent = (
        <nav
            className="lg:hidden fixed left-0 right-0 z-50 bg-cosmic-900 border-t border-white/10 px-2 pt-2.5"
            style={{
                // iOS Safari: Position above browser toolbar
                bottom: 0,
                paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom, 0px))',
                // Force hardware acceleration for iOS Safari
                WebkitTransform: 'translate3d(0, 0, 0)',
                transform: 'translate3d(0, 0, 0)',
                willChange: 'transform',
            }}
        >
            <div className="flex justify-around items-center">
                {navItems.map((item) => {
                    const Icon = item.icon;

                    // For dashboard/profile tabs, use button with onTabChange
                    if (item.id && onTabChange) {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => onTabChange(item.id!)}
                                className={`flex flex-col items-center gap-1 py-1.5 px-3 rounded-xl transition-colors ${isActive
                                    ? 'text-white'
                                    : 'text-purple-300/50 hover:text-purple-200'
                                    }`}
                            >
                                <div className={`p-1.5 rounded-lg transition-colors ${isActive
                                    ? 'bg-white/[0.12] text-purple-300'
                                    : ''
                                    }`}>
                                    <Icon size={20} />
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-wider">
                                    {item.label}
                                </span>
                            </button>
                        );
                    }

                    // For public pages, use Link
                    const isActive = location.pathname.startsWith(item.href!);
                    return (
                        <Link
                            key={item.label}
                            to={item.href!}
                            className={`flex flex-col items-center gap-1 py-1.5 px-3 rounded-xl transition-colors ${isActive
                                ? 'text-white'
                                : 'text-purple-300/50 hover:text-purple-200'
                                }`}
                        >
                            <div className={`p-1.5 rounded-lg transition-colors ${isActive
                                ? 'bg-white/[0.12] text-purple-300'
                                : ''
                                }`}>
                                <Icon size={20} />
                            </div>
                            <span className="text-[9px] font-bold uppercase tracking-wider">
                                {item.label}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </nav>
    );

    // Use portal to render directly into document.body for proper iOS Safari fixed positioning
    if (!mounted) return null;
    return createPortal(navContent, document.body);
};

export default MobileBottomNav;
